"use client";

import { useEffect, useState } from "react";
import { Box, styled, Fade, Skeleton, Stack } from "@mui/material";
import { useRouter } from "next/navigation";
import useDashboardStats from "@lib/dashboard/useDashboardStats";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import useRecruiterSettings from "@lib/user/useRecruiterSettings";
import useTeamStats from "@lib/dashboard/useTeamStats";
import { DASHBOARD_FOCUS, USER_ROLES } from "@lib/enums";
import { useDisplaySettings } from "@lib/settings/useDisplaySettings";
import Stats from "./parts/Stats";
import WelcomePanel from "./parts/WelcomePanel";
import CVUpload from "./parts/CVUpload/CVUpload";
import Notifications from "./parts/Notifications";
import TopLeads from "./parts/TopLeads";

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "48px",
  maxWidth: "1400px",
  padding: "40px 46px 40px 46px",

  [theme.breakpoints.down("md")]: {
    padding: "30px 20px",
  },
}));

// Skeleton loader component (hoisted to module scope so it isn't recreated on
// every Dashboard render — react-hooks/static-components).
const DashboardSkeleton = () => (
  <DashboardContainer>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={280}
        sx={{ borderRadius: "18px " }}
      />
      <Stack direction="row" spacing={3}>
        <Skeleton
          variant="rectangular"
          width="33%"
          height={180}
          sx={{ borderRadius: "18px " }}
        />
        <Skeleton
          variant="rectangular"
          width="33%"
          height={180}
          sx={{ borderRadius: "18px " }}
        />
        <Skeleton
          variant="rectangular"
          width="33%"
          height={180}
          sx={{ borderRadius: "18px " }}
        />
      </Stack>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={200}
        sx={{ borderRadius: "18px " }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={150}
        sx={{ borderRadius: "18px " }}
      />
    </Box>
  </DashboardContainer>
);

export default function Dashboard() {
  // state for sequential fade-ins
  const [showWelcome, setShowWelcome] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showCV, setShowCV] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTopLeads, setShowTopLeads] = useState(false);
  const router = useRouter();
  const {
    recruiter,
    plan,
    isLoading: isRecruiterLoading,
  } = useRecruiterDetails();
  const isTeamAdmin = recruiter?.role === USER_ROLES.TEAM_ADMIN;
  const isMember =
    recruiter?.role === USER_ROLES.MEMBER ||
    recruiter?.role === USER_ROLES.ADMIN ||
    recruiter?.role === USER_ROLES.SUPER_ADMIN;
  // Which record type the analytics are about, from the recruiter's settings
  const { settings } = useRecruiterSettings();
  const focus = settings?.dashboard_focus ?? DASHBOARD_FOCUS.FLOATS;
  const statsRange = useDisplaySettings((s) => s.teamDashboard.statsRange);
  const setStatsRange = useDisplaySettings((s) => s.setStatsRange);

  const {
    stats: memberStats,
    isLoading: isMemberLoading,
    isFetching: isMemberFetching,
  } = useDashboardStats({ range: statsRange, focus, enabled: isMember });

  const chartRange = useDisplaySettings(
    (s) => s.teamDashboard.chartRange,
  );
  const tableRange = useDisplaySettings(
    (s) => s.teamDashboard.tableRange,
  );
  const setTeamChartRange = useDisplaySettings((s) => s.setTeamChartRange);
  const setTeamTableRange = useDisplaySettings((s) => s.setTeamTableRange);

  const {
    fixedStats,
    chartStats,
    tableStats,
    fixedLoading,
    chartLoading,
    tableLoading,
  } = useTeamStats({
    statsRange,
    chartRange,
    tableRange,
    focus,
    enabled: isTeamAdmin,
  });

  const teamInitialLoading = isTeamAdmin && fixedLoading && !fixedStats;
  const isLoading =
    isRecruiterLoading || isMemberLoading || teamInitialLoading;
  const hasStats = isMember ? Boolean(memberStats) : isTeamAdmin;

  const changeRoute = (route) => {
    router.push(route);
  };

  // trigger sequential fades when loading completes
  useEffect(() => {
    if (!isLoading) {
      const timers = [
        setTimeout(() => setShowWelcome(true), 100),
        setTimeout(() => setShowStats(true), 400),
        setTimeout(() => setShowCV(true), 700),
        setTimeout(() => setShowNotifications(true), 1000),
        setTimeout(() => setShowTopLeads(true), 1300),
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [isLoading]);

  return isLoading ? (
    <DashboardSkeleton />
  ) : (
    <DashboardContainer>
      <Fade in={showWelcome} timeout={900} mountOnEnter unmountOnExit>
        <Box>
          <WelcomePanel
            plan={plan}
            recruiter={recruiter}
            changeRoute={changeRoute}
            focus={focus}
          />
        </Box>
      </Fade>

      {hasStats && (
        <Fade in={showStats} timeout={900} mountOnEnter unmountOnExit>
          <Box>
            <Stats
              isTeam={isTeamAdmin}
              focus={focus}
              stats={memberStats}
              fixedStats={fixedStats}
              chartStats={chartStats}
              tableStats={tableStats}
              chartLoading={chartLoading}
              tableLoading={tableLoading}
              statsLoading={isTeamAdmin ? fixedLoading : isMemberFetching}
              statsRange={statsRange}
              onStatsRangeChange={setStatsRange}
              chartRange={chartRange}
              tableRange={tableRange}
              onChartRangeChange={setTeamChartRange}
              onTableRangeChange={setTeamTableRange}
            />
          </Box>
        </Fade>
      )}

      <Fade in={showCV} timeout={900} mountOnEnter unmountOnExit>
        <Box>
          <CVUpload />
        </Box>
      </Fade>

      <Fade in={showNotifications} timeout={900} mountOnEnter unmountOnExit>
        <Box>
          <Notifications changeRoute={changeRoute} />
        </Box>
      </Fade>

      <Fade in={showTopLeads} timeout={900} mountOnEnter unmountOnExit>
        <Box>
          <TopLeads changeRoute={changeRoute} />
        </Box>
      </Fade>
    </DashboardContainer>
  );
}
